// Implements: F-UIX-007
/**
 * A compact "show more" (the mockup's collapsed groups inside a form): a real button with
 * `aria-expanded` and `aria-controls`, and a region hidden with the `hidden` attribute so the id the
 * button points at always exists. It starts closed and remembers nothing (unlike
 * `CollapsiblePanel`, a page-level panel whose state the person keeps).
 *
 * `forceOpen` keeps it open while something inside needs the person (a value that blocks the save):
 * hiding a problem behind a closed group would leave the person without a way to see it.
 *
 * The root is laid out by the caller (`className`): `display: contents` lets the trigger sit in one
 * cell of a grid and the region span the next row.
 */

import { useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { Icon } from "../atoms/Icon";
import styles from "./Disclosure.module.css";

export interface DisclosureProps {
  /** The region's id, which the button controls. */
  readonly id: string;
  /** The button's visible text, which is also its name. */
  readonly label: ReactNode;
  readonly forceOpen?: boolean | undefined;
  readonly className?: string | undefined;
  readonly triggerClassName?: string | undefined;
  readonly regionClassName?: string | undefined;
  readonly children: ReactNode;
}

export function Disclosure({
  id,
  label,
  forceOpen = false,
  className,
  triggerClassName,
  regionClassName,
  children,
}: DisclosureProps): ReactElement {
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;
  return (
    <div className={className}>
      <button
        type="button"
        className={[styles.trigger, triggerClassName].filter(Boolean).join(" ")}
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => {
          setOpen(!expanded);
        }}
      >
        <span className={expanded ? styles.chevronOpen : styles.chevron}>
          <Icon name="chevron-down" size="sm" />
        </span>
        <span className={styles.label}>{label}</span>
      </button>
      <div id={id} className={regionClassName} hidden={!expanded}>
        {children}
      </div>
    </div>
  );
}
